import type { CMSAssetType } from '../../types';
import { Body, Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, useToast } from '@workramp/ui';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router';
import { I18n } from '~/common/explicit-exports';
import { MixPanel } from '~/common';
import { CMS_BASE_URL, CMSAdminMixpanelEvents } from '../../constants';
import { useDeleteFile, useGetListAssets, useDeleteLink } from '../../api';

export type CMSDeleteModalProps = {
  assetType: CMSAssetType;
  assetId: string;
  isOpen: boolean;
  onClose: () => void;
};

const { APP_CONSTANTS } = window;
const { CMS_ASSET_TYPES } = APP_CONSTANTS;
export const CMSDeleteModal = ({ assetType, assetId, isOpen, onClose }: CMSDeleteModalProps) => {
  const TRANSLATED_CANCEL = I18n.t('js.admin.cms.actions.cancel');
  const TRANSLATED_DELETE = I18n.t('js.admin.cms.actions.delete');
  const TRANSLATED_TITLE = I18n.t('js.admin.cms.delete_modal_title', { assetType: assetType.toLowerCase() });
  const TRANSLATED_ERROR_MSG = I18n.t('js.admin.cms.delete_modal_error_msg', { assetType: assetType.toLowerCase() });
  const TRANSLATED_DESCRIPTION = I18n.t('js.admin.cms.delete_modal_description');
  const TRANSLATED_FILE_DELETED = I18n.t('js.admin.cms.delete_modal_file_deleted');
  const TRANSLATED_LINK_DELETED = I18n.t('js.admin.cms.delete_modal_link_deleted');

  const [isDisabled, setIsDisabled] = useState(false);
  const [showError, setShowError] = useState(false);

  const { open: openToast } = useToast();
  const navigate = useNavigate();

  const deleteFileMutation = useDeleteFile();
  const deleteLinkMutation = useDeleteLink();
  const { refetch: refetchCMSAssetPickerList } = useGetListAssets();

  const theme = useTheme();
  const onAssetDelete = async (type: CMSAssetType) => {
    setIsDisabled(true);

    if (type === CMS_ASSET_TYPES.FILE) {
      deleteFileMutation.mutate(assetId, {
        onSuccess: async () => {
          onClose();
          openToast({ children: TRANSLATED_FILE_DELETED });
          await refetchCMSAssetPickerList();
          MixPanel.track(CMSAdminMixpanelEvents.DELETE_ASSET, { asset_type: type });
          navigate(CMS_BASE_URL);
        },
        onError: () => {
          setShowError(true);
        },
        onSettled: () => {
          setIsDisabled(false);
        },
      });
    } else if (type === CMS_ASSET_TYPES.LINK) {
      deleteLinkMutation.mutate(assetId, {
        onSuccess: async () => {
          onClose();
          openToast({ children: TRANSLATED_LINK_DELETED });
          await refetchCMSAssetPickerList();
          MixPanel.track(CMSAdminMixpanelEvents.DELETE_ASSET, { asset_type: type });
          navigate(CMS_BASE_URL);
        },
        onError: () => {
          setShowError(true);
        },
        onSettled: () => {
          setIsDisabled(false);
        },
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" isDismissable onEntering={() => setShowError(false)}>
      <ModalHeader title={TRANSLATED_TITLE} description={TRANSLATED_DESCRIPTION} />
      {showError && (
        <ModalBody>
          <Body color={theme.colors.danger[400]} level="md">
            {TRANSLATED_ERROR_MSG}
          </Body>
        </ModalBody>
      )}
      <ModalFooter>
        <Row justify="flex-end" gapX={2}>
          <Col xs="auto">
            <Button data-testid="button-cancel" color="secondary" variant="text" onPress={onClose}>
              {TRANSLATED_CANCEL}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              data-testid="button-delete"
              color="danger"
              variant="outline"
              isDisabled={isDisabled}
              onPress={() => onAssetDelete(assetType)}
            >
              {TRANSLATED_DELETE}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
